import axios from 'axios';
import { getItem, setItem, getEntity } from './storage.handler';
import { endpoints } from './endpoints';

const PAGE_SIZE = 25;

const apiUrl = process.env.REACT_APP_API_URL;
//#region Authentication

/** Authentication header */

const token = window.localStorage.getItem('token');
axios.defaults.headers = {};
const pathNames = [
    'privacy-policy',
    'gallery',
    'contact-us',
    'services',
    'login',
    'payment',
    'all-quotes',
    'student-special',
    'services-all',
    'schedule',
    '',
];
const path = window.location.pathname.split('/')[1];

if (!token && path !== '/') {
    if (!pathNames.includes(path)) {
        window.location.replace('/');
    }
} else {
    axios.defaults.headers = {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
    };
}

const getCache = (cacheKey) => getItem(cacheKey);
const setCache = (cacheKey, data) => setItem(cacheKey, data);

const fetchSpecificPage = async (type, page, searchQuery = "") => {
    const endpoint = endpoints[type]; // Get the specific endpoint configuration based on type
    const url = searchQuery ? `${apiUrl}${endpoint.getMultipleUrl}/search/` : `${apiUrl}${endpoint.getMultipleUrl}/`;

    try {
        const response = await axios.get(url, {
            params: { q: searchQuery, page, page_size: PAGE_SIZE },
        });


        const { results, next, previous, count, total } = response.data;
        return { results, next, previous, count, total };
    } catch (error) {
        handleError(error, `Error fetching ${type} for specific page`);
        return { results: [], next: null, previous: null, count: 0 };
    }
};

export const getItemsForDropdown = async (type, id = null) => {
    const endpoint = endpoints[type]; // Get the specific endpoint configuration based on type
    const CACHE_KEY = endpoint.cacheKey; // **Same cache key for all queries**
    try {
        const response = await axios.get(`${apiUrl}${endpoint.getMultipleUrl}/`, {
            params: { id: id, page: 1, page_size: PAGE_SIZE },
        });

        const { results, next, previous, count } = response.data;

        // Replace existing cache instead of creating new keys
        const newCacheData = { results, next, previous, count };
        setCache(CACHE_KEY, newCacheData);

        return newCacheData;
    } catch (error) {
        handleError(error, `Error fetching ${type}`);
        return { results: [], next: null, previous: null, count: 0 };
    }
}

export const getItems = async (type, firstLoad, page = 1, searchQuery = "", reset = true) => {
    const endpoint = endpoints[type];
    const CACHE_KEY = endpoint.cacheKey;

    // Handle search query
    if (searchQuery) {
        const newData = await fetchSpecificPage(type, page, searchQuery);
        setCache(CACHE_KEY, newData);
        return newData;
    }

    // Handle pagination
    if (page !== 1) {
        return fetchSpecificPage(type, page);
    }

    const cachedData = getCache(CACHE_KEY);

    // If it's the first load and we have cache, return it
    if (firstLoad && cachedData && !reset) {
        return cachedData;
    }

    // If not resetting and cache exists, check if we can extend it
    if (!reset && cachedData) {
        const { results, next } = cachedData;

        if (next) {
            try {
                const response = await axios.get(next);
                const { results: newResults, next: newNext, previous: newPrevious, count: newCount } = response.data;

                const updatedCache = {
                    ...cachedData,
                    results: [...results, ...newResults],
                    next: newNext,
                    previous: newPrevious,
                    count: newCount,
                };

                setCache(CACHE_KEY, updatedCache);
                return updatedCache;
            } catch (error) {
                handleError(error, `Error fetching ${type} from next page`);
                return { results: [], next: null, previous: null, count: 0 };
            }
        }

        return cachedData;
    }

    // Otherwise, fetch fresh data (either cache is empty or reset is true)
    try {
        const response = await axios.get(`${apiUrl}${endpoint.getMultipleUrl}/`, {
            params: { page: 1, page_size: PAGE_SIZE },
        });

        const { results, next, previous, count, total } = response.data;
        const newCacheData = { results, next, previous, count, total };
        setCache(CACHE_KEY, newCacheData);
        return newCacheData;
    } catch (error) {
        handleError(error, `Error fetching ${type}`);
        return { results: [], next: null, previous: null, count: 0 };
    }
};

export const getItemById = async (type, id) => {
    id = parseInt(id);
    const endpoint = endpoints[type]; // Get the specific endpoint configuration based on type
    let item = null;

    // Get all cached data for the type (e.g., companies, leads)
    const cachedData = getCache(endpoint.cacheKey);

    if (cachedData) {
        // Search for the item in the cached results
        item = cachedData.results.find((item) => item.id === id);
    }

    if (!item) {
        // If the item is not found in cache, make an API call to fetch it
        try {
            const response = await axios.get(`${apiUrl}${endpoint.getUrl}/${id}/`);
            item = response.data;
        } catch (error) {
            handleError(error, `Error fetching ${type} with id ${id}`);
            item = null;
        }
    }

    return item;
};

export const updateItem = async (type, data) => {
    const endpoint = endpoints[type]; // Get the specific endpoint configuration based on type
    try {
        const response = await axios.put(`${apiUrl}${endpoint.getUrl}/update/${data.id}/`, data);
        return response.data;
    } catch (error) {
        handleError(error, `Error updating ${type}`);
        return null;
    }
};

export const createItem = async (type, data) => {
    const endpoint = endpoints[type]; // Get the specific endpoint configuration based on type
    try {
        const response = await axios.post(`${apiUrl}${endpoint.create}`, data);
        return response.data;
    } catch (error) {
        handleError(error, `Error creating ${type}`);
        return null;
    }
};

export const deleteItem = async (type, id) => {
    const endpoint = endpoints[type]; // Get the specific endpoint configuration based on type
    try {
        const response = await axios.delete(`${apiUrl}${endpoint.delete}/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, `Error deleting ${type} with id ${id}`);
        return null;
    }
};

//#endregion


//#region Quotes

export const createQuoteService = async (id) => {
    const response = await axios.get(apiUrl + `createservice-fromquote/${id}`);
    return response;
};

//#endregion

//#region Services

export const getServiceByStatus = async (status) => {
    const statusDict = {
        scheduled: 0,
        complete: 1,
        cancelled: 2,
    };

    await getServices();

    if (status === 'billed') {
        return getItem('services')
            .filter(
                (service) =>
                    service.billed === true &&
                    service.paid === false &&
                    service.serviceStatus === 1,
            )
            .sort((a, b) => {
                return a.invoiceNumber - b.invoiceNumber;
            });
    } else if (status === 'scheduled') {
        return getItem('services')
            .filter((service) => service.serviceStatus === statusDict[status])
            .sort((a, b) => {
                return new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time);
            });
    } else if (status === 'complete') {
        return getItem('services')
            .filter(
                (service) =>
                    service.serviceStatus === statusDict[status] ||
                    service.serviceStatus === statusDict['cancelled'],
            )
            .sort((a, b) => {
                return new Date(b.date) - new Date(a.date);
            });
    }
};

export const getService = async (id) => {
    await getServices();
    return getEntity('services', id);
};

export const getServices = async (force = false) => {
    if (!getItem('services') || force) {
        await axios
            .get(apiUrl + `services/`)
            .then((response) => setItem('services', response.data));
    }

    return getItem('services');
};

export const updateService = async (id, data) => {
    const response = await axios.put(apiUrl + `service/${id}/`, data);
    await getServices(true);
    return response;
};

export const createService = async (data) => {
    const response = await axios.post(apiUrl + `services/create`, data);
    await getServices(true);
    return response;
};

export const deleteService = async (id) => {
    const response = await axios.delete(apiUrl + `service/delete/${id}`);
    await getServices(true);
    return response;
};

export const duplicateService = async (id) => {
    const response = await axios.post(apiUrl + `duplicate-service/${id}/`);
    await getServices(true);
    return response;
};

//#endregion

//#region Invoices

export const getInvoice = async (id) => {
    await getInvoices();
    return getEntity('invoices', id);
};

export const getInvoices = async (force = false) => {
    if (!getItem('invoices') || force) {
        await axios
            .get(apiUrl + `invoices/`)
            .then((response) => setItem('invoices', response.data));
    }

    return getItem('invoices');
};

export const getAllInvoiceRecords = async () => {
    await axios
        .get(apiUrl + `get-all-invoices/`)
        .then((response) => setItem('invoices', response.data));
    return getItem('invoices');
};

export const followUp = async (id) => {
    const response = await axios.get(apiUrl + `invoice-follow-up/${id}`);
    return response;
};

export const createInvoice = async (data) => {
    const response = await axios.post(apiUrl + `invoices/create`, data);
    await getInvoices(true);
    return response;
};

export const deleteInvoice = async (id) => {
    const response = await axios.delete(apiUrl + `invoice/delete/${id}/`);
    await getInvoices(true);
    return response;
};
//#endregion

//#region TodaysService

export const todaysService = async (secret, force = false) => {
    if (!getItem('todaysService') || force) {
        await axios
            .get(apiUrl + `todays-services-summary/${secret}`)
            .then((response) => {
                if (response.status === 200) {
                    setItem('todaysService', response.data);
                }
            })
            .catch((error) => {
                if (error.response.status === 404) {
                    setItem('todaysService', []);
                }
            });
    }

    return getItem('todaysService');
};

//#endregion

//#region Quotes

export const commercialQuote = async (data) => {
    return axios.post(apiUrl + `commercial-quote/`, data);
};

export const residentialQuote = async (data) => {
    return axios.post(apiUrl + `residential-quote/`, data);
};

export const carpetQuote = async (data) => {
    return axios.post(apiUrl + `carpet-quote/`, data);
};

export const studentQuote = async (data) => {
    return axios.post(apiUrl + `student-quote/`, data);
};

export const groutQuote = async (data) => {
    return axios.post(apiUrl + `grout-quote/`, data);
};

export const paintQuote = async (data) => {
    return axios.post(apiUrl + `paint-quote/`, data);
};

export const floorQuote = async (data) => {
    return axios.post(apiUrl + `floor-quote/`, data);
};

export const quoteGenerator = async (data) => {
    const response = axios.post(apiUrl + `generate-quote/`, data);
    return response;
};

//#endregion

//#region Contact Us

export const contactFormSubmit = async (data) => {
    return axios.post(apiUrl + `contact/`, data);
};

//#endregion

//#region Dashboard

export const getDashboard = async (force = false) => {
    if (!getItem('dashboard') || force) {
        await axios
            .get(apiUrl + `generate-chart-data/`)
            .then((response) => setItem('dashboard', response.data));
    }

    return getItem('dashboard');
};

export const updateDashboardData = async () => {
    await axios.get(apiUrl + `sync-dashboard-data`);
};

//#endregion

//#region Payment

export const checkQuoteInvoice = async (data) => {
    return axios.post(apiUrl + `check-quote-invoice/`, data);
};

export const processPayment = async (data) => {
    return axios.post(apiUrl + `process-payment/`, data);
};

// # --------------------------------------------------------------------------------

//#region Contract

export const createContractServices = async (id) => {
    try {
        await axios.get(apiUrl + `create-contract-services/${id}`);
    } catch (error) {
        handleError(error, `Error creating services for contract ${id}`);
    }
};

//#endregion

export const dailyUpdate = async () => {
    try {
        await axios.get(apiUrl + `daily-update`);
    } catch (error) {
        handleError(error, `Error while calling daily update.`);
    }
};

export const receivePayment = async (id) => {
    try {
        await axios.put(apiUrl + `receive-payment/${id}/`);
    } catch (error) {
        handleError(error, `Error while calling daily update.`);
    }
};

export const downloadInvoiceFile = async (invoiceNumber) => {
    try {
        const response = await axios.get(apiUrl + `download-invoice/${invoiceNumber}`, {
            responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Get filename from response headers
        const disposition = response.headers['content-disposition'];
        let filename = 'invoice.pdf';
        if (disposition && disposition.includes('filename=')) {
            filename = disposition.split('filename=')[1].replace(/['"]/g, '');
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        handleError(error, `Error while downloading invoice.`);
    }
};

export const requestStatementCsv = async (clientId) => {
    try {
        const response = await axios.get(apiUrl + `invoices/download-csv/${clientId}`, {
            responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Extract filename from Content-Disposition header
        const disposition = response.headers['content-disposition'];
        let filename = 'statement.csv';
        if (disposition && disposition.includes('filename=')) {
            filename = disposition.split('filename=')[1].replace(/['"]/g, '');
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        handleError(error, 'Error while downloading account statement.');
    }
};

//#endregion

let setModalStateGlobal = () => {};  // Default empty function

export const setRecoilModalSetter = (setter) => {
    setModalStateGlobal = setter;
};

const handleError = (error, customMessage = "An error occurred while processing your request.") => {
    let errorMessage = error.response?.data?.message;

    if (!errorMessage) {
        if (error.message) {
            errorMessage = error.message;    
        }
        else {
            errorMessage = customMessage;
        }
    }

    setModalStateGlobal({
        isOpen: true,
        message: errorMessage
    });
};